"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.getRange = getRange;
exports.safeGetMonday = safeGetMonday;
exports.safeGetWeekRange = safeGetWeekRange;
exports.getSubRanges = getSubRanges;
exports.splitRangeByResolution = splitRangeByResolution;
exports.intersectRanges = intersectRanges;
exports.getCurrentMonthRange = getCurrentMonthRange;
exports.inferDynamicDateRangeType = inferDynamicDateRangeType;
exports.inferRangeType = inferRangeType;
exports.formatRange = formatRange;
exports.formatRangeShort = formatRangeShort;
const date_util_1 = require("./date.util");
function getRange(inp, period) {
    if (inp instanceof Date) {
        inp = (0, date_util_1.toDateString)(inp);
    }
    switch (period) {
        case "year":
            return (0, date_util_1.getYearRange)(inp);
        case "month":
            return (0, date_util_1.getMonthRange)(inp);
        case "week":
            return (0, date_util_1.getWeekRange)(inp);
        case "day":
            return (0, date_util_1.getDayRange)(inp);
    }
}
function safeGetMonday(inp) {
    const date = (0, date_util_1.parseDateString)(inp);
    if (!date)
        return null;
    date.setDate(date.getDate() - ((date.getDay() + 6) % 7));
    return (0, date_util_1.toDateString)(date);
}
function safeGetWeekRange(inp) {
    const from = safeGetMonday(inp);
    if (!from)
        return null;
    return {
        from,
        to: (0, date_util_1.dateAdd)(from, { days: 7 }),
    };
}
function getSubRanges(range, resolution = "month") {
    const ranges = [];
    let from = range.from;
    while (from < range.to) {
        const subRange = {
            from,
            to: (0, date_util_1.dateAdd)(from, { [`${resolution}s`]: 1 }),
        };
        if (subRange.to > range.to) {
            subRange.to = range.to;
        }
        ranges.push(subRange);
        from = subRange.to;
    }
    return ranges;
}
function splitRangeByResolution(range, resolution = "month") {
    const ranges = [];
    let date = range.from;
    while (date < range.to) {
        const currRange = getRange(date, resolution);
        ranges.push({
            from: range.from > currRange.from ? range.from : currRange.from, // get the max of the two dates
            to: range.to < currRange.to ? range.to : currRange.to, // get the min of the two dates
        });
        date = currRange.to;
    }
    return ranges;
}
function intersectRanges(range1, range2) {
    const effectiveStart = range1.from > range2.from ? range1.from : range2.from;
    const effectiveEnd = range1.to < range2.to ? range1.to : range2.to;
    if (effectiveEnd <= effectiveStart) {
        return null;
    }
    return { from: effectiveStart, to: effectiveEnd };
}
function getCurrentMonthRange() {
    return getRange(new Date(), "month");
}
function inferDynamicDateRangeType(range) {
    const fromDate = (0, date_util_1.parseDateString)(range.from);
    const toDate = (0, date_util_1.parseDateString)(range.to);
    if (!fromDate || !toDate) {
        return "custom";
    }
    const differenceDays = Math.round((toDate.getTime() - fromDate.getTime()) / (1000 * 60 * 60 * 24)); // TODO: replace with `day` once import doesnt break tests anymore
    // check if it matches a full week
    if (differenceDays === 7) {
        return "week";
    }
    // check if it matches a full month
    const fromMonthDays = new Date(fromDate.getFullYear(), fromDate.getMonth() + 1, 0).getDate();
    if (differenceDays === fromMonthDays) {
        return "month";
    }
    // check if it matches a full year
    const isOneYear = toDate.getFullYear() - fromDate.getFullYear() === 1;
    const isSameDate = range.from.substring(5) === range.to.substring(5);
    if (isOneYear && isSameDate) {
        return "year";
    }
    return "custom";
}
function inferRangeType({ from, to }) {
    // infer type based on our predefined ranges
    for (const type of ["month", "year", "week", "day"]) {
        const range = getRange(from, type);
        if (range.from === from && range.to === to) {
            return type;
        }
    }
    return "custom";
}
function formatRange(range, rangeType = inferRangeType(range)) {
    const date = (0, date_util_1.parseDateString)(range.from);
    switch (rangeType) {
        case "year":
            return date.getFullYear().toString();
        case "month":
            return (0, date_util_1.formatMonth)(date);
        case "day":
            return (0, date_util_1.formatDate)(date);
        case "week":
            return `KW ${(0, date_util_1.getWeekNumber)(range.from)}, ${new Date(range.from).getFullYear()} (${(0, date_util_1.formatDateShort)(range.from, false)} - ${(0, date_util_1.formatDateShort)((0, date_util_1.dateAdd)(range.to, { days: -1 }), false)})`;
        default: {
            const from = range.from;
            const to = (0, date_util_1.dateAdd)(range.to, { days: -1 });
            return `${(0, date_util_1.formatWeekDayShort)(from)}, ${(0, date_util_1.formatDate)(from)} - ${(0, date_util_1.formatWeekDayShort)(to)}, ${(0, date_util_1.formatDate)(to)}`;
        }
    }
}
function formatRangeShort(range, rangeType = inferRangeType(range)) {
    const date = (0, date_util_1.parseDateString)(range.from);
    if (!date) {
        throw new Error("Invalid date string");
    }
    switch (rangeType) {
        case "year":
            return date.getFullYear().toString();
        case "month":
            return (0, date_util_1.formatMonthShort)(date);
        case "day":
            return (0, date_util_1.formatDateShort)(date, true, true);
        case "week":
            return `(${(0, date_util_1.formatDateShort)(range.from, true, true)} - ${(0, date_util_1.formatDateShort)((0, date_util_1.dateAdd)(range.to, { days: -1 }), true, true)})`;
        default: {
            const from = range.from;
            const to = (0, date_util_1.dateAdd)(range.to, { days: -1 });
            return `${(0, date_util_1.formatDateShort)(from, true, true)} - ${(0, date_util_1.formatDateShort)(to, true, true)}`;
        }
    }
}
//# sourceMappingURL=range.util.js.map